<template>
    <PanelPage>
        <template v-slot:title>
            <SmartBreadcrumbs
                type="a"
                :pages="[
                    {
                        name: $tc('messages.battles', 2),
                        href: null,
                    },
                    {
                        name: $tc('messages.actions', 2),
                        href: null,
                    },
                ]"
            />
        </template>

        <div class="md:grid md:grid-cols-2 md:gap-6">
            <div
                class="w-full bg-gray-100 h-full bg-no-repeat bg-center bg-cover col-span-2"
                :style="`background-image: url(${battle.terrain.image_path});`"
            >
                <div class="grid grid-cols-3 md:grid-cols-3 md:gap-6">
                    <div class="p-4">
                        <img class="w-full" :src="battle.enemy.image_path" alt="enemy" ref="enemy" />
                        <ProgressBar :percent="percent" color="red" v-if="battle.is_enemy_health_shown">
                            <div>
                                {{ battle.enemy_health_left }} /
                                {{ battle.enemy_health_total }}
                            </div>
                        </ProgressBar>
                    </div>
                    <div class="col-span-2 order-first">
                        &nbsp;
                        <div class="p-4" v-if="battle.is_reward_shown">
                            <div class="text-white">
                                {{ $t('messages.prize_to_be_shared') }}
                                ({{
                                    battle.is_reward_evenly_split
                                        ? $t('messages.reward_evenly_split')
                                        : $t('messages.reward_not_evenly_split')
                                }}) :
                            </div>
                            <div class="flex space-x-2 mt-3">
                                <div>
                                    <img class="h-6 w-6" src="/assets/gamification/currency/coin.svg" alt="emerald" />
                                </div>
                                <div class="text-white">
                                    {{ battle.coins_amount }}
                                </div>
                            </div>
                            <div class="flex space-x-2 mt-3" v-if="battle.enemy_damage">
                                <div class="text-white">
                                    {{ $tc('messages.damage') }}:
                                    {{ battle.enemy_damage }}
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>

        <div class="mt-5" v-if="canPerformBattleActions">
            <div class="grid grid-cols-2 lg:grid-cols-6 gap-6">
                <div v-for="item in items" class="bg-gray-100 p-2 rounded-lg" :key="item.id">
                    <img :src="item.image_path" alt="" />
                    <div>
                        {{ item.name }}
                    </div>
                    <div class="text-gray-600 text-sm">
                        {{ item.options_description }}
                    </div>
                    <div class="flex gap-x-2">
                        <div v-if="item.description">
                            <Tippy>
                                <button>
                                    <IconInfoCircle class="w-6 h-6 text-gray-400" />
                                </button>
                                <template #content>
                                    {{ item.description }}
                                </template>
                            </Tippy>
                        </div>
                        <div v-if="item.uses_stock">
                            <Tippy>
                                <button>
                                    <IconBox class="w-6 h-6 text-gray-400" v-if="item.stock_amount" />
                                    <IconBoxOff class="w-6 h-6 text-gray-400" v-else />
                                </button>
                                <template #content>
                                    {{ item.stock_message }}
                                </template>
                            </Tippy>
                        </div>
                        <div v-if="item.max_total_uses">
                            <Tippy>
                                <button>
                                    <IconInfinityOff class="w-6 h-6 text-gray-400" />
                                </button>
                                <template #content>
                                    {{ item.max_total_uses_message }}
                                </template>
                            </Tippy>
                        </div>
                    </div>
                    <div class="flex justify-between items-center">
                        <div>
                            <BadgePill :color="item.count ? 'indigo' : 'gray'"> &times;{{ item.count }} </BadgePill>
                        </div>
                        <div>
                            <UseWeaponSlideOverWidget
                                :weapon="item"
                                :set="gamificationSet"
                                :gamification-score-card="gamificationScoreCard"
                            />
                        </div>
                    </div>
                    <div class="flex justify-between items-center mt-1" v-if="item.can.purchase">
                        <div>
                            <BadgePill color="yellow">
                                {{ item.price }}
                            </BadgePill>
                        </div>
                        <InertiaLinkButton as="button" :href="item.purchase_url" type="white" method="post">
                            {{ $t('messages.buy') }}
                        </InertiaLinkButton>
                    </div>
                    <div class="flex justify-between items-center mt-1" v-if="item.required_level_description">
                        {{ $t('messages.level_required') }}:
                        {{ item.required_level_description }}
                    </div>
                </div>
            </div>
        </div>

        <div class="rounded-md bg-blue-50 p-4 mt-5">
            <div class="flex">
                <div class="shrink-0">
                    <!-- Heroicon name: solid/information-circle -->
                    <svg
                        class="h-5 w-5 text-blue-400"
                        xmlns="http://www.w3.org/2000/svg"
                        viewBox="0 0 20 20"
                        fill="currentColor"
                        aria-hidden="true"
                    >
                        <path
                            fill-rule="evenodd"
                            d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z"
                            clip-rule="evenodd"
                        />
                    </svg>
                </div>
                <div class="ml-3 flex-1 md:flex md:justify-between">
                    <p class="text-sm text-blue-700">
                        {{ battle.event_ending_date_message }}
                    </p>
                </div>
            </div>
        </div>

        <div v-if="battle.max_actions_per_student" class="mt-3">
            {{
                $t('messages.this_event_allows_X_actions', {
                    count: battle.max_actions_per_student,
                })
            }}
        </div>

        <div class="flex flex-col mt-5">
            <div class="-my-2 py-2 overflow-x-auto sm:-mx-6 sm:px-6 lg:-mx-8 lg:px-8">
                <div
                    class="align-middle inline-block min-w-full shadow overflow-hidden sm:rounded-lg border-b border-gray-200"
                >
                    <table class="min-w-full divide-y divide-gray-200">
                        <thead>
                            <tr>
                                <th
                                    class="max-w-xs px-6 py-3 bg-gray-50 text-left text-xs leading-4 font-medium text-gray-500 uppercase tracking-wider"
                                >
                                    {{ $t('messages.name') }}
                                </th>
                                <th
                                    class="max-w-xs px-6 py-3 bg-gray-50 text-left text-xs leading-4 font-medium text-gray-500 uppercase tracking-wider"
                                >
                                    {{ $tc('messages.actions', 2) }}
                                </th>
                                <th
                                    class="max-w-xs px-6 py-3 bg-gray-50 text-left text-xs leading-4 font-medium text-gray-500 uppercase tracking-wider"
                                >
                                    {{ $t('messages.when') }}
                                </th>
                            </tr>
                        </thead>
                        <tbody>
                            <tr
                                :class="index % 2 === 0 ? 'bg-white' : 'bg-gray-50'"
                                v-for="(action, index) in battle.actions"
                                :key="action.id"
                            >
                                <td class="px-6 py-4 whitespace-nowrap text-sm leading-5 text-gray-500">
                                    <img
                                        :src="action.avatar_or_default"
                                        :alt="action.full_name"
                                        :title="action.full_name"
                                        class="rounded-full h-12"
                                    />
                                </td>
                                <td class="px-6 py-4 whitespace-nowrap text-sm leading-5 text-gray-500">
                                    <img :src="action.action_image" class="h-12" :alt="action.action_name" />
                                </td>
                                <td class="px-6 py-4 whitespace-nowrap text-sm leading-5 text-gray-500">
                                    {{ action.diffForHumans }}
                                </td>
                            </tr>
                        </tbody>
                    </table>
                </div>
            </div>
        </div>
    </PanelPage>
    <VictoryModal :message="$t('messages.battle_finished_message')" v-if="meta.info.is_just_finished" />
</template>

<script>
import Layout from '@/Shared/Layout.vue';
import PanelPage from '@/Shared/PanelPage.vue';
import BadgePill from '@/components/BadgePill.vue';
import InertiaLinkButton from '@/components/InertiaLinkButton.vue';
import ProgressBar from '@/components/Gamification/ProgressBar.vue';
import { IconBox, IconBoxOff, IconInfinityOff, IconInfoCircle } from '@tabler/icons-vue';
import { Tippy } from 'vue-tippy';
import VictoryModal from '@/components/pageSpecific/VictoryModal.vue';
import UseWeaponSlideOverWidget from './partials/UseWeaponSlideOverWidget.vue';

export default {
    components: {
        VictoryModal,
        PanelPage,
        BadgePill,
        InertiaLinkButton,
        ProgressBar,
        Tippy,
        IconBox,
        IconBoxOff,
        IconInfinityOff,
        IconInfoCircle,
        UseWeaponSlideOverWidget,
    },
    layout: Layout,
    name: 'Show',
    props: ['meta', 'battle', 'items', 'gamificationSet', 'gamificationScoreCard'],
    data() {
        return {};
    },
    computed: {
        canPerformBattleActions() {
            if (this.battle.max_actions_per_student === null) {
                return true;
            }

            if (this.battle.max_actions_per_student > this.battle.actions.length) {
                return true;
            }

            return false;
        },
        percent: function () {
            return Math.floor((this.battle.enemy_health_left / this.battle.enemy_health_total) * 100);
        },
    },
};
</script>

<style scoped>
details summary::-webkit-details-marker,
details summary::marker {
    display: none;
    content: '';
}
</style>
